// # NBME Question Bank Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { Lock, Edit, Save, Cancel } from '@mui/icons-material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import './nbme2.css';
import { 
  FaCalendarAlt, 
  FaClock,
  FaBook,
  FaClipboardCheck,
  FaChartLine,
  FaTasks,
  FaUserClock,
  FaListAlt,
  FaUserMd,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaRegCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// # Import Firebase
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

// # Import step icons
import stepTwoIcon from '../../../aneo_media/mentee/two.png';

// # Define chart colors
const CHART_COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f1c40f', '#9b59b6'];

// # Define status options
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed'];

// # Define NBME exams for step 2
const NBME_EXAMS = [
  "NBME 9",
  "NBME 10",
  "NBME 11",
  "NBME 12",
  "NBME 13",
  "NBME 14",
  "NBME 15",
  "NBME 16",
  "New Free 120",
  "Online Free 120"
];

function NBMEQBank({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [qbankData, setQbankData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [tempData, setTempData] = useState({});
  const [selectedChart, setSelectedChart] = useState('bar');
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  // # Access is always granted for admin, subscription check is handled by ProtectedRouteUser
  const stepAccess = isAdmin ? true : true;

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load data from Firestore
  useEffect(() => {
    const loadQBankData = async () => {
      if (!targetUserId) return;

      try {
        // # Only load data if we have access
        if (stepAccess) {
          const docRef = doc(db, 's2_mentee', targetUserId);
          const docSnap = await getDoc(docRef);
          const nbmeData = docSnap.data()?.nbme || {};

          // # Initialize with loaded data or default values
          const initialData = NBME_EXAMS.reduce((acc, exam) => {
            acc[exam] = {
              status: nbmeData[exam]?.status || 'Not Started',
              percentage: nbmeData[exam]?.percentage || 0,
              correct: nbmeData[exam]?.correct || 0,
              incorrect: nbmeData[exam]?.incorrect || 0,
              weaknesses: nbmeData[exam]?.weaknesses || []
            };
            return acc;
          }, {});

          setQbankData(initialData);
        }
      } catch (error) {
        console.error('Error loading qbank data:', error);
      }
    };

    loadQBankData();
  }, [targetUserId, stepAccess]);

  // # Calculate dashboard stats
  const calculateStats = (stepData) => {
    if (!stepData) return {
      totalCompleted: 0,
      totalExams: 0,
      totalCorrect: 0,
      totalIncorrect: 0,
      totalWeakPoints: 0,
      totalPercentage: 0,
      averagePercentage: 0
    };

    const exams = Object.entries(stepData);
    const totalCompleted = exams.filter(([_, exam]) => exam?.status === 'Completed').length;
    const totalExams = exams.length;
    const totalCorrect = exams.reduce((sum, [_, exam]) => sum + (exam?.correct || 0), 0);
    const totalIncorrect = exams.reduce((sum, [_, exam]) => sum + (exam?.incorrect || 0), 0);
    const totalWeakPoints = exams.reduce((sum, [_, exam]) => sum + ((exam?.weaknesses || []).length || 0), 0);
    const totalPercentage = totalCorrect + totalIncorrect > 0 
      ? ((totalCorrect / (totalCorrect + totalIncorrect)) * 100).toFixed(1)
      : 0;
    const averagePercentage = exams.length > 0 
      ? exams.reduce((sum, [_, exam]) => sum + (exam?.percentage || 0), 0) / exams.length 
      : 0;

    return {
      totalCompleted,
      totalExams,
      totalCorrect,
      totalIncorrect,
      totalWeakPoints,
      totalPercentage,
      averagePercentage
    };
  };

  // # Prepare chart data
  const getChartData = (stepData) => {
    return Object.entries(stepData).map(([exam, data]) => ({
      name: exam,
      percentage: data.percentage
    }));
  };

  // # Toggle edit mode
  const toggleEditMode = () => {
    if (editMode) {
      // # Cancel changes
      setTempData({ ...qbankData });
    } else {
      // # Enter edit mode
      setTempData({ ...qbankData });
    }
    setEditMode(!editMode);
  };

  // # Handle input change
  const handleInputChange = (exam, field, value) => {
    if (field === 'weaknesses') {
      setTempData(prev => ({
        ...prev,
        [exam]: {
          ...prev[exam],
          weaknesses: value
        }
      }));
      return;
    }

    // # For percentage field, convert to number
    const numValue = field === 'percentage' ? (value === '' ? '' : Number(value)) : value;
    
    setTempData(prev => ({
      ...prev,
      [exam]: {
        ...prev[exam],
        [field]: numValue
      }
    }));
  };

  // # Update saveChanges to include Firestore save
  const saveChanges = async () => {
    const calculatedData = { ...tempData };

    // # Calculate for each exam
    Object.keys(calculatedData).forEach(exam => {
      const data = calculatedData[exam];
      
      // # Skip if no numeric values entered
      if (!data.correct && !data.incorrect && !data.percentage) {
        return;
      }

      // # Convert empty strings to 0
      const correct = data.correct === '' ? 0 : Number(data.correct || 0);
      const incorrect = data.incorrect === '' ? 0 : Number(data.incorrect || 0);
      const percentage = data.percentage === '' ? 0 : Number(data.percentage || 0);

      // # Calculate missing values
      if (correct && incorrect) {
        // # Calculate percentage from correct and incorrect
        const total = correct + incorrect;
        calculatedData[exam].percentage = total > 0 ? ((correct / total) * 100).toFixed(1) : 0;
      } else if (correct && percentage) {
        // # Calculate incorrect from correct and percentage
        calculatedData[exam].incorrect = Math.round((correct * 100 / percentage) - correct);
      } else if (incorrect && percentage) {
        // # Calculate correct from incorrect and percentage
        calculatedData[exam].correct = Math.round((incorrect * percentage) / (100 - percentage));
      }

      // # Ensure all values are numbers, not strings
      calculatedData[exam].correct = Number(calculatedData[exam].correct || 0);
      calculatedData[exam].incorrect = Number(calculatedData[exam].incorrect || 0);
      calculatedData[exam].percentage = Number(calculatedData[exam].percentage || 0);
    });

    // # Update local state
    setQbankData(calculatedData);

    // # Save to Firestore
    await saveToFirestore(calculatedData);

    // # Exit edit mode
    setEditMode(false);
  };

  // # Prepare pie chart data with total correct/incorrect only
  const getPieChartData = (stepData) => {
    const totalCorrect = Object.values(stepData).reduce((sum, data) => sum + (data.correct || 0), 0);
    const totalIncorrect = Object.values(stepData).reduce((sum, data) => sum + (data.incorrect || 0), 0);
    
    return [
      {
        name: 'Correct',
        value: totalCorrect,
        color: '#2ecc71' // Green for correct
      },
      {
        name: 'Incorrect',
        value: totalIncorrect,
        color: '#e74c3c' // Red for incorrect
      }
    ].filter(item => item.value > 0); // Only show segments with values
  };

  // # Prepare line chart data
  const getLineChartData = (stepData) => {
    return Object.entries(stepData).map(([exam, data]) => ({
      name: exam,
      progress: data.percentage
    }));
  };

  // # Modified chart rendering with better sizing
  const renderChart = (data) => {
    const margin = { top: 20, right: 30, left: 20, bottom: 100 };
    const containerWidth = document.querySelector('.nbme2_chart_container')?.clientWidth || 600;
    const chartWidth = Math.min(Math.max(getChartData(data).length * 80, containerWidth - 40), containerWidth);
    
    switch (selectedChart) {
      case 'bar':
        return (
          <div className="nbme2_chart_scroll_container">
            <BarChart 
              data={getChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="percentage" fill="#3498db" />
            </BarChart>
          </div>
        );
      case 'pie':
        return (
          <div className="nbme2_chart_scroll_container">
            <div className="nbme2_pie_chart_container">
              <PieChart
                width={Math.min(800, containerWidth - 40)}
                height={600}
                margin={{ top: 40, right: 30, left: 30, bottom: 40 }}
              >
                <Pie
                  data={getPieChartData(data)}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={Math.min(180, (containerWidth - 120) / 4)}
                  label={({ name, value, percent }) => 
                    `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                  }
                >
                  {getPieChartData(data).map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`Total: ${value}`, name]}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  wrapperStyle={{ 
                    paddingTop: '40px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                />
              </PieChart>
            </div>
          </div>
        );
      case 'line':
        return (
          <div className="nbme2_chart_scroll_container">
            <LineChart 
              data={getLineChartData(data)}
              margin={margin}
              width={chartWidth}
              height={300}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="progress" stroke="#3498db" />
            </LineChart>
          </div>
        );
      default:
        return null;
    }
  };

  // # Save data to Firestore
  const saveToFirestore = async (data) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's2_mentee', targetUserId);

      // # Get current document data
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.data() || {};

      // # Update only the nbme field
      await setDoc(docRef, {
        ...currentData,
        nbme: data
      }, { merge: true });

    } catch (error) {
      console.error('Error saving qbank data:', error);
      // You might want to show an error message to the user here
    }
  };

  // # Get current data based on edit mode
  const currentData = editMode ? tempData : qbankData;
  const stats = calculateStats(currentData);
  const isLocked = !stepAccess;

  return (
    <div className="nbme2_container">
      <SharedHeader />
      <div className="nbme2_main">
        <div className="nbme2_content_wrapper">
          <h1 className="nbme2_title">
            NBME Practice Exams
            {isMentor && <span className="nbme2_mentor_pill">Mentor</span>}
          </h1>
          
          {/* Step 2 section */}
          <div className="nbme2_step_section">
            <div className="nbme2_step_header">
              <img 
                src={stepTwoIcon} 
                alt="Step 2" 
                className="nbme2_step_icon" 
              />
              <h2>NBME - Step 2 Practice Exams</h2>
              {!isLocked && (
                <button 
                  className="nbme2_edit_button"
                  onClick={() => {
                    if (editMode) {
                      saveChanges();
                    } else {
                      toggleEditMode();
                    }
                  }}
                >
                  {editMode ? <Save /> : <Edit />}
                </button>
              )}
              {editMode && (
                <button 
                  className="nbme2_cancel_button"
                  onClick={toggleEditMode}
                >
                  <Cancel />
                </button>
              )}
              {isLocked && (
                <div className="nbme2_lock_icon">
                  <Lock />
                  <span className="nbme2_lock_text">Subscription Required</span>
                </div>
              )}
            </div>

            {!isLocked && (
              <div className="nbme2_step_content">
                {/* Table */}
                <div className="nbme2_table_container">
                  <table className="nbme2_table">
                    <thead>
                      <tr>
                        <th>Exam</th>
                        <th>Status</th>
                        <th>Percentage</th>
                        <th>Correct</th>
                        <th>Incorrect</th>
                        <th>Weak Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {NBME_EXAMS.map((exam) => (
                        <tr key={exam}>
                          <td>{exam}</td>
                          <td>
                            {editMode ? (
                              <select
                                className={`nbme2_status_select nbme2_status_${(currentData[exam]?.status || 'Not Started').toLowerCase().replace(/ /g, '_')}`}
                                value={currentData[exam]?.status || 'Not Started'}
                                onChange={(e) => handleInputChange(exam, 'status', e.target.value)}
                              >
                                {STATUS_OPTIONS.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`nbme2_status_${(currentData[exam]?.status || 'Not Started').toLowerCase().replace(/ /g, '_')}`}>
                                {currentData[exam]?.status || 'Not Started'}
                              </span>
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={currentData[exam]?.percentage === 0 ? '' : currentData[exam]?.percentage || ''}
                                  onChange={(e) => handleInputChange(exam, 'percentage', e.target.value)}
                                  className="nbme2_edit_input"
                                />
                              </div>
                            ) : (
                              <span>
                                {currentData[exam]?.percentage || 0}%
                              </span>
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                value={currentData[exam]?.correct === 0 ? '' : currentData[exam]?.correct || ''}
                                onChange={(e) => handleInputChange(exam, 'correct', e.target.value)}
                                className="nbme2_edit_input"
                              />
                            ) : (
                              currentData[exam]?.correct || 0
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <input
                                type="number"
                                value={currentData[exam]?.incorrect === 0 ? '' : currentData[exam]?.incorrect || ''}
                                onChange={(e) => handleInputChange(exam, 'incorrect', e.target.value)}
                                className="nbme2_edit_input"
                              />
                            ) : (
                              currentData[exam]?.incorrect || 0
                            )}
                          </td>
                          <td>
                            <div className="nbme2_weakness_cell">
                              {editMode ? (
                                <div className="nbme2_weakness_edit_container">
                                  <div className="nbme2_weakness_list">
                                    {currentData[exam]?.weaknesses?.length > 0 && (
                                      <ul>
                                        {currentData[exam]?.weaknesses.map((weakness, index) => (
                                          <li key={index}>
                                            {weakness}
                                            <button
                                              className="nbme2_remove_weakness_btn"
                                              onClick={() => {
                                                const updatedWeaknesses = currentData[exam]?.weaknesses.filter((_, i) => i !== index);
                                                handleInputChange(exam, 'weaknesses', updatedWeaknesses);
                                              }}
                                            >
                                              ×
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  <div className="nbme2_weakness_input_container">
                                    <input
                                      type="text"
                                      className="nbme2_edit_input nbme2_weakness_input"
                                      placeholder="Type new weakness"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                          const currentWeaknesses = currentData[exam]?.weaknesses || [];
                                          handleInputChange(
                                            exam,
                                            'weaknesses',
                                            [...currentWeaknesses, e.target.value.trim()]
                                          );
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                    <button 
                                      className="nbme2_add_weakness_btn"
                                      onClick={(e) => {
                                        const input = e.target.parentElement.querySelector('input');
                                        if (input.value.trim()) {
                                          const currentWeaknesses = currentData[exam]?.weaknesses || [];
                                          handleInputChange(
                                            exam,
                                            'weaknesses',
                                            [...currentWeaknesses, input.value.trim()]
                                          );
                                          input.value = '';
                                        }
                                      }}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="nbme2_weakness_list">
                                  {currentData[exam]?.weaknesses?.length > 0 ? (
                                    <ul>
                                      {currentData[exam]?.weaknesses.map((weakness, index) => (
                                        <li key={index}>{weakness}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span>None</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Dashboard */}
                <div className="nbme2_dashboard">
                  <div className="nbme2_dashboard_stats">
                    <div className="nbme2_stat_card">
                      <h3>Progress</h3>
                      <p>{stats.totalCompleted} / {stats.totalExams}</p>
                    </div>
                    <div className="nbme2_stat_card">
                      <h3>Total Correct</h3>
                      <p>{stats.totalCorrect}</p>
                    </div>
                    <div className="nbme2_stat_card">
                      <h3>Total Incorrect</h3>
                      <p>{stats.totalIncorrect}</p>
                    </div>
                    <div className="nbme2_stat_card">
                      <h3>Total Percentage</h3>
                      <p>{stats.totalPercentage}%</p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="nbme2_charts">
                    <div className="nbme2_chart_controls">
                      <button 
                        className={`nbme2_chart_type_btn ${selectedChart === 'bar' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('bar')}
                      >
                        Bar Chart
                      </button>
                      <button 
                        className={`nbme2_chart_type_btn ${selectedChart === 'pie' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('pie')}
                      >
                        Pie Chart
                      </button>
                      <button 
                        className={`nbme2_chart_type_btn ${selectedChart === 'line' ? 'active' : ''}`}
                        onClick={() => setSelectedChart('line')}
                      >
                        Line Chart
                      </button>
                    </div>
                    <div className="nbme2_chart_container">
                      <h3>Performance by Exam</h3>
                      {renderChart(currentData)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="nbme2_footer_wrapper">
        <div className="nbme2_footer_content">
          <DashboardFooter />
        </div>
      </div>
    </div>
  );
}

export default NBMEQBank;
